import React, { useEffect, useState } from 'react';
import styles from './enrichment-domain-information.module.sass';
import { Typography, Tooltip, Table } from 'antd';
import ProgressBar from '../../../../shared-components/ProgressBar';
import { InfoCircleFilled } from '@ant-design/icons';
import { ColumnTitle } from '../../../../shared-components/ColumnTitle';
import LoadingSkeleton from '../../../../shared-components/LoadingSkeleton';
import Tag from '../../../../shared-components/Tag';
import { formatDate, formatDaysTag } from '../../../../redux/utils/date';
import { STATUS } from '../../../../redux/constants';

function EnrichmentDomainInformation({
  newData,
  statusNew,
  oldData,
  statusOld,
  infraTag,
  infraTagStatus,
  props,
}) {
  const { Title, Text, Paragraph } = Typography;
  const newEnrichmentData = newData?.response;
  const infraTagData = infraTag?.response;
  const domainInfo = newEnrichmentData?.domaininfo;
  const dgaScore =
    newEnrichmentData?.domain_string_frequency_probability
      ?.dga_probability_score;

  const [userTags, setUserTags] = useState();

  useEffect(() => {
    if (
      oldData?.feeds_containing_ioc?.length > 0 &&
      statusOld === STATUS.LOADED
    ) {
      getUserTags(oldData?.feeds_containing_ioc);
    }
  }, [oldData, statusOld]);

  function getUserTags(feed) {
    if (!feed?.length) return;
    let tagsArray = [];
    for (let i in feed) {
      tagsArray.push({
        user_tags: feed?.[i]?.user_tags,
        state: feed?.[i]?.state,
      });
    }
    tagsArray = tagsArray?.flat();
    setUserTags(tagsArray);
  }

  const infratagTooltip = (
    <>
      <Paragraph>
        The Infratag is an automatically generated tag that provides an
        at-a-glance summary of the infrastructure used by a domain.
      </Paragraph>
      <Paragraph>The Infratag consists of the following parts:</Paragraph>
      <Paragraph className="m-0">• MX records</Paragraph>
      <Paragraph className="m-0">• NS records</Paragraph>
      <Paragraph className="m-0">
        • The AS name hosting the domain’s primary IPs
      </Paragraph>
      <Paragraph className="m-0">
        • The name of the registrar used to register the domain
      </Paragraph>
    </>
  );

  const columns = [
    {
      title: <ColumnTitle columnName="Threat Name" />,
      dataIndex: 'name',
      key: 'name',
      width: 150,
      render: (value, params) => {
        if (params?.tooltip) {
          return (
            <>
              {value + ' '}
              <Tooltip placement="top" title={params?.tooltip}>
                <InfoCircleFilled />
              </Tooltip>
            </>
          );
        } else {
          return value;
        }
      },
    },
    {
      title: <ColumnTitle columnName="Value" />,
      dataIndex: 'value',
      key: 'value',
      render: (value, params) => {
        if (statusNew === STATUS.LOADED && statusOld === STATUS.LOADED) {
          if (params?.usertag) {
            //check if there are user tags
            if (value && value?.some((item) => item?.user_tags?.length > 0)) {
              //this one is looping through the array of object (of tags)
              return value?.map((elem) =>
                //looping through the user tags per feed/collection
                elem?.user_tags?.map((tag) => (
                  <Tag
                    tag={{ name: tag }}
                    tagColor={'gold'}
                    canClick={
                      props.mySubscriptionPlan?.has_access_to_threat_ranking
                        ? true
                        : false
                    }
                    isTagManuallyCreated={false}
                    canRemove={false}
                    extraParams={
                      elem?.state === 'COLLECTION'
                        ? '&feed_state=COLLECTION'
                        : ''
                    }
                    filterProfileToBeCleared={true}
                    showTooltip={
                      !props.mySubscriptionPlan?.has_access_to_threat_ranking
                    }
                    tooltipText={
                      <>
                        Filtering on tags is not part of this subscription plan.
                        To upgrade your subscription contact us at{' '}
                        <a target="_blank" href="mailto:sales@silentpush.com">
                          sales@silentpush.com
                        </a>
                        .
                      </>
                    }
                  />
                )),
              );
            }
            //if no user tag is found, return "-"
            else {
              return '-';
            }
          } else {
            return value;
          }
        } else {
          return <LoadingSkeleton />;
        }
      },
    },
  ];

  function returnTableData({ loading }) {
    return [
      {
        key: 1,
        name: 'User Tags',
        usertag: true,
        tooltip: (
          <Paragraph className="m-0">
            Individual observable’s tags user added
          </Paragraph>
        ),
        value: userTags ? userTags : [],
      },
      {
        key: 2,
        name: 'Infratag',
        usertag: false,
        tooltip: infratagTooltip,
        value: loading ? (
          <LoadingSkeleton />
        ) : (
          <>
            <Text strong className={styles.block}>
              {infraTagData?.infratag?.tag}
            </Text>
          </>
        ),
      },
      {
        key: 3,
        name: 'First Seen',
        usertag: false,
        tooltip: (
          <Paragraph className="m-0">
            The date when this domain was first seen in the zone files
          </Paragraph>
        ),
        value: loading ? (
          <LoadingSkeleton />
        ) : domainInfo?.first_seen ? (
          <Text strong>{formatDate(domainInfo?.first_seen)}</Text>
        ) : (
          <Text strong>-</Text>
        ),
      },
      {
        key: 4,
        name: 'Last Seen',
        usertag: false,
        tooltip: (
          <Paragraph className="m-0">
            The date when the domain was most recently seen in the zone files
          </Paragraph>
        ),
        value: loading ? (
          <LoadingSkeleton />
        ) : domainInfo?.last_seen ? (
          <Text strong>{formatDate(domainInfo?.last_seen)}</Text>
        ) : (
          <Text strong>-</Text>
        ),
      },
      {
        key: 5,
        name: 'Age',
        usertag: false,
        tooltip: (
          <Paragraph className="m-0">
            The number of days since the domain was first seen in the zone files
          </Paragraph>
        ),
        value: loading ? (
          <LoadingSkeleton />
        ) : (
          <>
            {domainInfo?.age === 0 || domainInfo?.age ? (
              <>
                <Text strong>{domainInfo?.age}</Text>
                <Text className="me-1">
                  {formatDaysTag(domainInfo?.age, true)}
                </Text>
              </>
            ) : (
              <Text className="me-1" strong>
                -
              </Text>
            )}
            {(domainInfo?.age_score === 0 || domainInfo?.age_score) && (
              <ProgressBar
                bgcolor={domainInfo?.age_score}
                width={'72'}
                completed={domainInfo?.age_score}
              />
            )}
          </>
        ),
      },
      {
        key: 6,
        name: 'DGA',
        usertag: false,
        tooltip: (
          <Paragraph className="m-0">
            How likely it is that a domain was created by an automated
            generation algorithm
          </Paragraph>
        ),
        value: loading ? (
          <LoadingSkeleton />
        ) : dgaScore || dgaScore === 0 ? (
          <>
            <Text strong>{dgaScore}</Text>
            <Text className="me-1"> score</Text>
            <ProgressBar bgcolor={dgaScore} width={'72'} completed={dgaScore} />
          </>
        ) : (
          <Text strong>-</Text>
        ),
      },
    ];
  }

  return (
    <div
      id="enrichmentDomainInformation"
      className={styles.enrichment_domain_information}
    >
      <Title level={4}>Domain Information</Title>
      <Table
        columns={columns}
        dataSource={returnTableData({
          loading: statusNew === STATUS.LOADING,
        })}
        pagination={false}
      />
    </div>
  );
}

export default EnrichmentDomainInformation;
